/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/invoiceUploadSettings.scss';

import {AwesomeIcon, ChangeFunction, SelectItemShape} from '@dvrd/dvr-controls';
import React, {Fragment, MouseEventHandler, useContext, useRef} from 'react';
import {ActivateHandle, CustomerType, InvoiceRuleType, InvoiceUploadRule} from "../../util/interfaces";
import {BolmateButton, BolmateInput, BolmateNumberInput, BolmateSelect} from "../controls";
import {customerTypeItems} from "../../util/emailStepUtil";
import StepCountriesController from "../controls/country/select/countrySelect/countrySelectController";
import classNames from 'classnames';
import BolmateSwitch from "../controls/switch/bolmateSwitch";
import InfoIcon from "../icon/infoIcon";
import {AppContext} from "../misc/appContext";

interface Props {
    onAddRule: MouseEventHandler;
    onUpdateRule: (rule: InvoiceUploadRule, callback?: (success: boolean) => void) => void;
    onDeleteRule: (rule: InvoiceUploadRule) => MouseEventHandler;
    onSubmit: MouseEventHandler;
    onChangeProactiveUpload: ChangeFunction<boolean>;
    onChangeAutoRespond: ChangeFunction<boolean>;
    onChangeAutoRespondOld: ChangeFunction<boolean>;
    onChangeOldNumPrefix: ChangeFunction<string>;
    onChangeOldNumStart: ChangeFunction<string>;
    onChangeNotification: ChangeFunction<boolean>;
    onChangeNotifyMail: ChangeFunction<string>;
    notifyMail: string;
    loading: boolean;
    rules: Array<InvoiceUploadRule>;
    hasChanged: boolean;
    respondOldEnabled: boolean;
    proactiveEnabled: boolean;
    respondEnabled: boolean;
    notificationEnabled: boolean;
    oldNumPrefix: string;
    oldNumStart: string;
    noSubTitle?: boolean;
}

const typeItems: SelectItemShape[] = [
    {label: 'Alleen', value: InvoiceRuleType.WHITELIST},
    {label: 'Niet', value: InvoiceRuleType.BLACKLIST}
];

export default function InvoiceUploadSettings(props: Props) {
    const context = useContext(AppContext);
    const {user} = context.userContext;
    const {
        loading, rules, onAddRule, onDeleteRule, onSubmit, onUpdateRule, hasChanged, onChangeProactiveUpload,
        onChangeAutoRespond, onChangeNotifyMail, notifyMail, onChangeNotification, onChangeAutoRespondOld,
        onChangeOldNumStart, oldNumPrefix, onChangeOldNumPrefix, respondOldEnabled, oldNumStart, notificationEnabled,
        respondEnabled, proactiveEnabled, noSubTitle
    } = props;

    function renderInfo() {
        return (
            <div className='info-container'>
                <InfoIcon className='info-icon'/>
                <p className='info-message'>
                    <b>Proactief uploaden</b><br/>
                    Met proactief uploaden zorg je ervoor dat voor elke nieuwe order, de factuur na voltooiing
                    van order wordt geüpload. Klanten kunnen de btw-factuur dan vinden en downloaden in hun
                    Bol klantportaal. Zo voorkom je factuuraanvragen.
                    <br/><br/>
                    <b>Automatisch aanvragen beantwoorden</b><br/>
                    Indien de factuur van een order nog niet is geüpload, kan een klant de factuur aanvragen.
                    Met deze optie wordt er automatisch gereageerd op deze aanvraag met de bijbehorende factuur.
                    Door ook de optie proactief uploaden aan te vinken, zorg je ervoor dat er steeds minder van
                    dit soort aanvragen zijn, omdat de factuur al klaar zal staan en men deze niet meer
                    aanvraagt.
                    <br/><br/>
                    <b>Aanbevolen</b><br/>
                    Wij raden aan zowel proactief uploaden als
                    automatisch beantwoorden aan te vinken.
                </p>
            </div>
        )
    }

    function renderToggles() {
        return (
            <div className='invoice-upload-setting toggles'>
                {noSubTitle ? (
                    <div className='with-info'>
                        <label className='tab-title'>Automatisch uploaden</label>
                        {renderInfo()}
                    </div>
                ) : <label className='tab-title'>Automatisch uploaden</label>}
                {!noSubTitle && <div className='with-info'>
                    <p className='description'>Dezelfde instellingen als rechtsboven</p>
                    {renderInfo()}
                </div>}
                <div className='toggles-container'>
                    <div className='auto-toggle'>
                        <BolmateSwitch value={proactiveEnabled} onChange={onChangeProactiveUpload} activeGreen/>
                        <label>Proactief uploaden</label>
                    </div>
                    <div className='auto-toggle'>
                        <BolmateSwitch value={respondEnabled} onChange={onChangeAutoRespond} activeGreen/>
                        <label>Auto aanvragen beantwoorden</label>
                    </div>
                    {renderAutoRespondOld()}
                </div>
            </div>
        )
    }

    function renderAutoRespondOld() {
        return (
            <div className='respond-old-container'>
                <div className='auto-toggle'>
                    <BolmateSwitch value={respondOldEnabled} onChange={onChangeAutoRespondOld} activeGreen/>
                    <label>Auto aanvragen beantwoorden voor oude orders</label>
                    <div className='info-container'>
                        <InfoIcon className='info-icon'/>
                        <p className='info-message'>
                            <b>Factuuraanvragen voor oude orders</b><br/>
                            Jouw klanten kunnen voor orders tot 2 jaar terug een factuur aanvragen. Het is
                            mogelijk dat wij deze order niet in het systeem hebben, omdat jouw Bolmate account
                            toen nog niet bestond.
                            <br/><br/>We kunnen deze factuuraanvraag wel automatisch beantwoorden met een
                            factuur upload, maar doordat de Belastingdienst vereist dat jouw factuurnummering
                            chronologisch oploopt, kunnen we hiervoor niet het standaard factuurnummer
                            gebruiken.
                            <br/><br/>Je kunt daarom een andere factuurnummerreeks kiezen voor de facturen voor
                            oude orders, op deze manier blijven de nummers chronologisch oplopend.
                        </p>
                    </div>
                </div>
                {respondOldEnabled && (
                    <div className='old-invoice-num-settings'>
                        <div className='row sm'>
                            <label>Factuurnummer voor oude orders</label>
                        </div>
                        <div className='row'>
                            <BolmateInput value={oldNumPrefix} onChange={onChangeOldNumPrefix} label='Voorvoegsel'/>
                            <BolmateNumberInput value={oldNumStart} onChange={onChangeOldNumStart} label='Starten met'
                                                wholeNumbers disableSymbols/>
                        </div>
                    </div>
                )}
            </div>
        )
    }

    function renderRules() {
        return (
            <div className='invoice-upload-setting'>
                <label className='tab-title'>Alleen uploaden bij bepaalde voorwaarde</label>
                <p className='description'>Facturen worden alleen automatisch geüpload als aan deze voorwaarde wordt
                    voldaan.</p>
                <div className='invoice-upload-body'>
                    <div className='rules'>
                        {rules.map((rule: InvoiceUploadRule, index: number) =>
                            <Rule onUpdateRule={onUpdateRule} onDeleteRule={onDeleteRule} rule={rule} index={index}
                                  loading={loading}/>)}
                    </div>
                </div>
                <div className='btn-container btn-2'>
                    <BolmateButton onClick={onAddRule} label='Regel toevoegen' disabled={loading} secondary/>
                    <BolmateButton onClick={onSubmit} label='Opslaan' disabled={loading || !hasChanged}/>
                </div>
            </div>
        )
    }

    function renderNotification() {
        return (
            <div className='invoice-upload-setting'>
                <label className='tab-title'>Notificatie e-mail bij vereiste handmatige actie</label>
                <p className='description'>Stuur mij een e-mail notificatie als een nieuwe factuuraanvraag niet
                    automatisch beantwoord kan worden.<br/>Bijvoorbeeld omdat de factuur niet in de Bolmate database
                    zit, als de order van vóór begin gebruik Bolmate is.
                </p>
                <div className='notification-container'>
                    <label>Stuur mij een e-mail notificatie</label>
                    <BolmateSwitch value={notificationEnabled} onChange={onChangeNotification} activeGreen/>
                    {notificationEnabled &&
                        <BolmateInput onChange={onChangeNotifyMail} value={notifyMail} disabled={loading}
                                      inputProps={{type: 'email', placeholder: user?.email}}
                                      label='Notificatie e-mailadres' className='email-field'/>}
                </div>
            </div>
        )
    }

    return (
        <div className='invoice-upload-settings'>
            {renderToggles()}
            {renderRules()}
            {renderNotification()}
            <div className='btn-container'>
                <BolmateButton onClick={onSubmit} label='Opslaan' disabled={loading || !hasChanged}/>
            </div>
        </div>
    )
}

interface RuleProps {
    onUpdateRule: (rule: InvoiceUploadRule) => void;
    onDeleteRule: (rule: InvoiceUploadRule) => MouseEventHandler;
    rule: InvoiceUploadRule;
    index: number;
    loading: boolean;
}

function Rule(props: RuleProps) {
    const {onUpdateRule, rule, index, loading, onDeleteRule} = props;
    const countryRef = useRef<ActivateHandle>(null);

    function onClickCountries() {
        countryRef.current?.activate(rule.countries ?? []);
    }

    function onChangeRuleType(ruleType: InvoiceRuleType) {
        rule.rule_type = ruleType;
        onUpdateRule(rule);
    }

    function onChangeCustomerType(customerType: CustomerType) {
        rule.customer_type = customerType;
        onUpdateRule(rule);
    }

    function onChangeCountries(countries: Set<string>, allSelected: boolean, callback: (close: boolean) => void) {
        // const countryCodes = Array.from(countries).map((country: string) => getCountryCode(country));
        if (allSelected) rule.countries = [];
        else rule.countries = Array.from(countries);
        onUpdateRule(rule);
        callback(true);
    }

    function renderCountry() {
        const count = rule.countries?.length ?? 0;
        let label: string;
        if (!count) label = 'Alle landen';
        else if (count === 1) label = rule.countries![0];
        else label = `${count} landen`;
        return (
            <section>
                <label className='fixed-text'>in</label>
                {renderVarButton(label, onClickCountries)}
            </section>
        )
    }

    function renderVarButton(label: string, onClick: MouseEventHandler) {
        const clickable = !loading;
        return (
            <div className={classNames('var-button', !clickable && 'disabled')}
                 onClick={clickable ? onClick : undefined}>
                <label className='button-label'>{label}</label>
            </div>
        )
    }

    return (
        <Fragment key={index}>
            <div className='upload-rule'>
                <section>
                    <BolmateSelect value={rule.rule_type} onChange={onChangeRuleType} items={typeItems}
                                   className='select-field' itemsPosition='top'/>
                </section>
                <section>
                    <label className='fixed-text'>automatisch uploaden voor</label>
                    <BolmateSelect onChange={onChangeCustomerType} value={rule.customer_type} items={customerTypeItems}
                                   className='select-field' itemsPosition='top'/>
                </section>
                {renderCountry()}
                <AwesomeIcon name='trash-alt' className='btn-delete' onClick={onDeleteRule(rule)}/>
            </div>
            <StepCountriesController ref={countryRef} loading={loading} onSubmit={onChangeCountries}/>
        </Fragment>
    )
}